import { Contact, Link, ListFilter, QrCode, Search, UserSearch } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import DataTablePagination from "../../DataTablePagination";
import Spacing from "../../Spacing";
import { getPractice, numberWithCommas, generatePatientSurveyData, datePickerFormat, notify, handelCopyLink, handleQRScanPage,  can_visit, debounce, setSecureData } from "../../../helpers";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { fetchBulkPatientSurverForm, fetchPatientSurveyForm } from "../../../services/forms";
import PatientSurveyDataFormTable from "../../DataTable/PatientSurveyDataTable";
import FormViewDetailsModal from "../../Modal/FormViewDetailsModal";
import { getLocation } from "../../../services/appointment";


export default function PatientSurveyForm({
  selectedPracticeChange,
  fetchChangePracticeData,
}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [allLocation, setAllLocation] = useState([]);
  const [formViewId, setFormViewId] = useState(null)
  const [formSize, setFormSizes] = useState('A4')
  const [formView, setFormView] = useState(false)
  const [dataLoading, setDataLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filter, setFilter] = useState({
    account_id: account_id,
    location_id: location_id,
    page: 1,
    per_page: 10,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    sex: "",
    option2_tel_no: "",
    order_by: [],
  });

  const defaultFilterData = {
    account_id: account_id,
    location_id: location_id,
    from_dos: "",
    to_dos: "",
  };
  const [printFilter, setPrintFilter] = useState(defaultFilterData);

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchPatientSurveyForm(getFilter);
      if (response) {
        const generateData = generatePatientSurveyData(response.data.results);
        setData(generateData);
        setDataCount({
          totalCount: response.data.count,
          totalPages: response.data.total_pages,
        });
        if (checkedIds.length < 1) {
          setSelectAll(false);
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    loadLocations(account_id);
  }, [location_id, account_id]);

  useEffect(() => {
    loadData(filter);
  }, [filter]);

  const handleCheckboxChange = (id) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
  };

  const handleSelectAll = () => {
    const newCheckedIds = selectAll ? [] : data.map((item) => item.id);
    setCheckedIds(newCheckedIds);

    const newData = data.map((row) => ({
      ...row,
      checked: !selectAll,
    }));
    setData(newData);
    setSelectAll(!selectAll);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handleOutsideClick = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      !event.target.closest(".react-datepicker")
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    loadLocations(account_id);
  }, []);

  const loadLocations = async (id, accountId) => {
    try {
      const locationResponse = await getLocation(id, accountId);
      setAllLocation(locationResponse.data || []);
    } catch (error) {
      // console.log("🚀 ~ loadLocations ~ error:", error);
    }
  };

  const handleChangeDosDate = (name, value) => {
    setPrintFilter({
      ...printFilter,
      [name]: datePickerFormat(value),
    });
  };
  const handleChangeFilter = (e) => {
    const { name, value } = e.target;
    setPrintFilter({
      ...printFilter,
      [name]: name === "location_id" ? parseInt(value, 10) : value
    });
  };

  const handleResetFilters = () => {
    setDataLoading(true);
    loadData(filter);
    setPrintFilter(defaultFilterData);
    setIsOpen(false);
  };
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handlePatientSurveyPrint = async (e) => {
    e.preventDefault();
    e.target.disabled = true;

    const response = await fetchBulkPatientSurverForm(printFilter);
    
    if (response.detail) {
      notify('error', response.detail);
    } else { 
      setIsOpen(false);
      
      await setSecureData(
        'surveyPrintData',
        JSON.stringify({
          results: response?.data?.results,
          filter: printFilter,
        })
      );

      const url = `/forms/patient-survey-form/print-status?size=A4`;
      window.open(url, '_blank');
    } 
    e.target.disabled = false; 
  };

  const handleFormView = (formId) => {
    setFormViewId(formId)
    setFormView(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };
  const handleFormPrint = (formId) => {
    const url = `/forms/patient-survey-form/print?form_id=${formId}&size=A4`;
    window.open(url, '_blank');
  };

  useDocumentTitle("Patient Survey Form");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_teal_bg docpro_white_color">
            <Contact width={20} height={20} />
          </i>
          Patient Survey Form
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <div className="flex items-center">
            <form className="docpro_search_form width_100">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>

          <div className="docpro_data_table_btns p-0">
            {(getPractice("website") !== null && getPractice("website")) && (
              <>
                <button 
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={()=>handleQRScanPage('patient_survey')}
                >
                  <QrCode width={20} height={20} />
                  Form QR code
                </button>
                <button 
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={()=>handelCopyLink(`${getPractice("website")}/patient-survey-form`)}
                >
                  <Link width={20} height={20} />
                  Form link
                </button>
              </>
            )}
            {can_visit("administrator") && (
              <div ref={dropdownRef} style={{ position: "relative" }}>
                <button className="docpro_filter docpro_fw_500" onClick={toggleDropdown}>
                  <ListFilter width={16} height={16} />
                  Print
                </button>
                <>
                  {isOpen && (
                    <div className="docpro_filter_dropdown_body docpro_size_md">
                      <h4 className="docpro_fs_18 docpro_mb_0">Select Information</h4>
                      <Spacing lg="16" sm="16" />
                      <form onSubmit={handlePatientSurveyPrint} autoComplete="off">
                        <label className="docpro_fw_500 docpro_primary_color">
                          Select Location
                        </label>
                        <select
                          name="location_id"
                          className="docpro_form_field size_lg"
                          onChange={handleChangeFilter}
                          value={printFilter.location_id}
                        >
                          {allLocation.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                          ))}
                        </select>
                        <Spacing lg="16" sm="16" />
                        <label className="docpro_fw_500 docpro_primary_color">
                          Date range (DOS)
                        </label>
                        <div className="docpro_input_group">
                          <DatePicker
                            className="docpro_form_field size_lg"
                            name="from_dos"
                            placeholderText="mm/dd/yyyy"
                            selected={printFilter.from_dos}
                            onChange={(date) =>
                              handleChangeDosDate("from_dos", date)
                            }
                          />
                          -
                          <DatePicker
                            className="docpro_form_field size_lg"
                            name="to_dos"
                            placeholderText="mm/dd/yyyy"
                            selected={printFilter.to_dos}
                            onChange={(date) =>
                              handleChangeDosDate("to_dos", date)
                            }
                          />
                        </div>
                        <Spacing lg="28" sm="28" />
                        <div className="docpro_filter_dropdown_btns">
                          <button
                            className="docpro_text_btn docpro_accent_color docpro_fw_600"
                            onClick={handleResetFilters}
                          >
                            Clear
                          </button>
                          <button
                            className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
                            type="submit"
                          >
                            <UserSearch /> Generate
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </>
              </div>
            )}
          </div>
        </div>
        <PatientSurveyDataFormTable
          tableData={data}
          handleSelectAll={handleSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          checkedIds={checkedIds}
          checkSelected={checkSelected}
          handleFormView={handleFormView}
          handleFormPrint={handleFormPrint}
          reloadData={loadData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
        />
        {data.length > 0 && !dataLoading && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {formView && (
        <FormViewDetailsModal 
          formId = {formViewId}
          formSize = {formSize}
          formView = {formView}
          setFormView = {setFormView}
          formType = {'patient_survey'}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
